/**
 * PlasmaCorePurchaseModal — покупка/установка Plasma Core на контракт.
 *
 * Два состояния:
 *   1) 'confirm' — информация о PC + стоимость + кнопка "Установить"
 *   2) 'activating' — WOW-анимация установки (5+ секунд):
 *      - Круговые волны расходятся от центра
 *      - Частицы энергии собираются в core
 *      - Финальная вспышка + текст "PLASMA CORE ACTIVATED"
 *      - Haptic feedback на каждом этапе
 *
 * iPhone Liquid Glass стиль, plasma-cyan акцент.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Zap, Shield, Clock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import { lunaApi } from '@/services/api';
import { fmtNum } from '@/lib/format';
import plasmaCoreIcon from '@/assets/plasma-core.webp';

interface PlasmaCorePurchaseModalProps {
  open: boolean;
  /** Все contractId без PC — установим на все сразу */
  contractIds: string[];
  slotName: string;
  slotPrice: number;
  userBalance?: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function PlasmaCorePurchaseModal({
  open,
  contractIds,
  slotName,
  slotPrice,
  userBalance = 0,
  onClose,
  onSuccess,
}: PlasmaCorePurchaseModalProps) {
  const { t } = useTranslation();
  const { haptic } = useTelegramWebApp();
  const uiSounds = useUISounds();
  const [phase, setPhase] = useState<'confirm' | 'activating' | 'done'>('confirm');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const phaseTimersRef = useRef<number[]>([]);

  const count = contractIds.length;
  const pcCostPerSlot = Math.round(slotPrice * 0.2 * 100) / 100;
  const pcCost = pcCostPerSlot * count;
  const canAfford = userBalance >= pcCost;

  // Reset on close — все состояния, иначе второй PC после первого
  // откроется со stuck "ACTIVATING..." кнопкой (isLoading оставался true)
  useEffect(() => {
    if (!open) {
      setPhase('confirm');
      setError('');
      setIsLoading(false);
      phaseTimersRef.current.forEach(clearTimeout);
      phaseTimersRef.current = [];
    }
  }, [open]);

  const schedule = (fn: () => void, ms: number) => {
    phaseTimersRef.current.push(window.setTimeout(fn, ms));
  };

  const handleActivate = useCallback(async () => {
    if (contractIds.length === 0 || phase !== 'confirm' || isLoading) return;
    setError('');
    setIsLoading(true);

    try {
      // Активируем PC на ВСЕХ контрактах без PC
      for (const cid of contractIds) {
        await lunaApi.plasmaCore.activate(cid);
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message || t('pcPurchase.activationError');
      setError(msg);
      setIsLoading(false);
      return;
    }

    // Успех — запускаем анимацию
    setPhase('activating');
    haptic.success();
    uiSounds.plasmaCoreActivate();

    // Этапы анимации с haptic — 8 секунд
    schedule(() => haptic.light(), 600);      // Сканирование
    schedule(() => haptic.light(), 1200);     // Подключение
    schedule(() => haptic.light(), 1800);     // Инициализация
    schedule(() => haptic.medium(), 2600);    // Ядро активируется
    schedule(() => haptic.light(), 3400);     // Энергия нарастает
    schedule(() => haptic.light(), 4200);     // Стабилизация
    schedule(() => haptic.medium(), 5200);    // Plasma Core заряжен
    schedule(() => haptic.success(), 6500);   // Финальная вспышка
    schedule(() => haptic.success(), 7200);   // Подтверждение

    // Финал — 8 секунд
    schedule(() => {
      setPhase('done');
      haptic.success();
      uiSounds.plasmaCoreDone();
    }, 8000);
  }, [contractIds, phase, haptic, isLoading, schedule]);

  const handleDone = useCallback(() => {
    haptic.success();
    onSuccess();
    onClose();
  }, [onSuccess, onClose, haptic]);

  // Escape to close (only in confirm)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase === 'confirm') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, phase]);

  const plasmaAccent = 'var(--color-plasma-400)';
  const plasmaGlow = 'var(--color-plasma-500)';

  // iOS — полный размер; Android — окно на 20% меньше.
  const isNonIOS = document.documentElement.classList.contains('platform-non-ios');
  const modalScale = isNonIOS ? 0.8 : 1;
  const modalScaleStart = isNonIOS ? 0.74 : 0.92;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={phase === 'confirm' ? () => { uiSounds.bellClose(); onClose(); } : undefined}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md [.platform-non-ios_&]:bg-black/90 [.platform-non-ios_&]:backdrop-blur-none" />

          <motion.div
            className="relative w-full max-w-sm rounded-[28px]"
            style={{
              background: 'linear-gradient(180deg, var(--glass-fill-strong) 0%, var(--glass-fill) 100%)',
              border: `1px solid color-mix(in oklab, ${plasmaAccent} 30%, var(--glass-stroke-strong))`,
              boxShadow: `var(--glass-shadow), 0 0 100px -20px ${plasmaGlow}`,
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
              minHeight: 420,
            }}
            initial={{ y: 40, opacity: 0, scale: modalScaleStart }}
            animate={{ y: 0, opacity: 1, scale: modalScale }}
            exit={{ y: 40, opacity: 0, scale: modalScaleStart }}
            transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ─── CONFIRM PHASE ─── */}
            {phase === 'confirm' && (
              <div className="p-6">
                {/* Close */}
                <button
                  onClick={() => { haptic.light(); onClose(); }}
                  className="absolute top-4 right-4 p-2 rounded-xl text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-all active:scale-[0.9] z-10"
                  style={{ background: 'var(--glass-fill)' }}
                >
                  <X size={16} strokeWidth={2} />
                </button>

                {/* Icon */}
                <div className="flex justify-center mb-5">
                  <div className="relative">
                    <img
                      src={plasmaCoreIcon}
                      alt="Plasma Core"
                      className="h-24 w-24"
                      style={{
                        filter: `drop-shadow(0 0 20px ${plasmaGlow})`,
                      }}
                    />
                    <div
                      className="absolute inset-0 animate-pulse rounded-full"
                      style={{
                        background: `radial-gradient(circle, color-mix(in oklab, ${plasmaGlow} 20%, transparent) 0%, transparent 60%)`,
                      }}
                    />
                  </div>
                </div>

                {/* Title */}
                <div className="text-center mb-6">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-muted)] font-mono mb-1">
                    {t('pcPurchase.installOnSlot', { slotName })}
                  </p>
                  <h2 className="font-display text-[24px] font-extrabold tracking-tight" style={{ color: plasmaAccent }}>
                    Plasma Core
                  </h2>
                  <p className="text-[12px] text-[var(--text-secondary)] mt-2 leading-relaxed">
                    {t('pcPurchase.autoAndPlus25')}
                  </p>
                </div>

                {/* Features */}
                <div className="space-y-2.5 mb-6">
                  <FeatureRow icon={<Zap size={14} />} text={t('pcPurchase.featureAutoClaim')} accent={plasmaAccent} />
                  <FeatureRow icon={<Shield size={14} />} text={t('slotDetail.pcPlus25')} accent={plasmaAccent} />
                  <FeatureRow icon={<Clock size={14} />} text={t('pcPurchase.featureRecharge')} accent={plasmaAccent} />
                </div>

                {/* Cost */}
                <div
                  className="rounded-2xl p-4 mb-5"
                  style={{
                    background: 'var(--glass-fill)',
                    border: '1px solid var(--glass-stroke)',
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-[var(--text-muted)] font-mono uppercase tracking-[0.12em]">
                      {t('pcPurchase.installationCost')}
                    </span>
                    <span className="font-display text-[20px] font-extrabold tabular-nums" style={{ color: plasmaAccent }}>
                      {fmtNum(pcCost)} <span className="text-[12px] text-[var(--text-muted)]">USD₮</span>
                    </span>
                  </div>
                  <p className="text-[10px] text-[var(--text-disabled)] mt-1">
                    {count > 1
                      ? t('pcPurchase.contractsMul', { count, cost: fmtNum(pcCostPerSlot) })
                      : t('pcPurchase.contractPct', { price: fmtNum(slotPrice) })}
                  </p>
                </div>

                {/* CTA */}
                <button
                  type="button"
                  onClick={handleActivate}
                  disabled={isLoading || !canAfford}
                  className="w-full rounded-2xl py-4 font-display text-[14px] font-extrabold tracking-[0.04em] uppercase flex items-center justify-center gap-2 transition-all active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    background: `linear-gradient(180deg, ${plasmaAccent}, color-mix(in oklab, ${plasmaAccent} 60%, #0a4060))`,
                    color: '#0a0a0f',
                    boxShadow: `0 20px 40px -10px color-mix(in oklab, ${plasmaAccent} 55%, transparent), inset 0 1px 0 0 rgba(255,255,255,0.45)`,
                  }}
                >
                  <Zap size={16} strokeWidth={2.4} />
                  {isLoading ? t('pcPurchase.activating') : !canAfford ? t('pcPurchase.needAmount', { amount: fmtNum(pcCost) }) : count > 1 ? t('pcPurchase.installAll', { count }) : t('pcPurchase.installOne')}
                </button>

                {error && (
                  <p className="text-[11px] text-[var(--status-negative)] text-center mt-3">{error}</p>
                )}
              </div>
            )}

            {/* ─── ACTIVATING PHASE — WOW Animation ─── */}
            {phase === 'activating' && (
              <ActivationAnimation plasmaAccent={plasmaAccent} plasmaGlow={plasmaGlow} icon={plasmaCoreIcon} />
            )}

            {/* ─── DONE PHASE ─── */}
            {phase === 'done' && (
              <div className="relative flex items-center justify-center py-10 px-6">
                {/* Success burst */}
                <motion.div
                  className="absolute inset-0"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={{
                    background: `radial-gradient(circle at 50% 45%, color-mix(in oklab, ${plasmaGlow} 20%, transparent) 0%, transparent 50%)`,
                  }}
                />

                <motion.div
                  className="relative z-10 flex flex-col items-center text-center px-6"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.6, ease: [0.32, 1.4, 0.32, 1] }}
                >
                  {/* Success icon with glow behind */}
                  <div className="relative mb-4">
                    <div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `radial-gradient(circle, color-mix(in oklab, ${plasmaGlow} 35%, transparent) 0%, transparent 70%)`,
                        transform: 'scale(1.8)',
                      }}
                    />
                    <motion.img
                      src={plasmaCoreIcon}
                      alt="Plasma Core"
                      className="relative h-20 w-20"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.3, type: 'spring', stiffness: 300, damping: 15 }}
                    />
                  </div>

                  <p className="text-[10px] uppercase tracking-[0.25em] font-mono mb-2" style={{ color: plasmaAccent }}>
                    {t('pcPurchase.installedKicker')}
                  </p>
                  <h2 className="font-display text-[28px] font-extrabold tracking-tight text-[var(--text-primary)]">
                    Plasma Core
                  </h2>
                  <p className="font-display text-[18px] font-bold mt-1" style={{ color: plasmaAccent }}>
                    {t('pcPurchase.activated')}
                  </p>

                  <p className="text-[11px] text-[var(--text-secondary)] mt-4 leading-relaxed">
                    {t('pcPurchase.firstPayoutHint')}
                    <br />{t('pcPurchase.rechargeLater')}
                  </p>

                  <button
                    type="button"
                    onClick={handleDone}
                    className="mt-6 w-full rounded-2xl py-3.5 text-[13px] font-display font-extrabold uppercase tracking-[0.08em] transition-transform active:scale-[0.95]"
                    style={{
                      background: 'var(--glass-fill)',
                      border: `1px solid color-mix(in oklab, ${plasmaAccent} 35%, var(--glass-stroke))`,
                      color: 'var(--text-primary)',
                    }}
                  >
                    {t('pcPurchase.done')}
                  </button>
                </motion.div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** 8-секундная WOW-анимация активации Plasma Core */
function ActivationAnimation({ plasmaAccent, plasmaGlow, icon }: {
  plasmaAccent: string; plasmaGlow: string; icon: string;
}) {
  const { t } = useTranslation();
  const STATUS_PHASES = [
    { text: t('pcPurchase.step1'), delay: 0 },
    { text: t('pcPurchase.step2'), delay: 1.5 },
    { text: t('pcPurchase.step3'), delay: 3 },
    { text: t('pcPurchase.step4'), delay: 4.5 },
    { text: t('pcPurchase.step5'), delay: 6 },
    { text: t('pcPurchase.step6'), delay: 7.2 },
  ];

  return (
    <div className="relative h-[380px] flex items-center justify-center overflow-hidden">
      {/* Пульсирующий фон */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            `radial-gradient(circle at 50% 50%, color-mix(in oklab, ${plasmaGlow} 8%, transparent) 0%, transparent 50%)`,
            `radial-gradient(circle at 50% 50%, color-mix(in oklab, ${plasmaGlow} 20%, transparent) 0%, transparent 60%)`,
            `radial-gradient(circle at 50% 50%, color-mix(in oklab, ${plasmaGlow} 12%, transparent) 0%, transparent 55%)`,
            `radial-gradient(circle at 50% 50%, color-mix(in oklab, ${plasmaGlow} 25%, transparent) 0%, transparent 65%)`,
          ],
        }}
        transition={{ duration: 8, ease: 'easeInOut' }}
      />

      {/* 7 расходящихся колец — медленнее, 2 волны */}
      {[0, 1, 2, 3, 4, 5, 6].map(i => (
        <motion.div
          key={`ring-${i}`}
          className="absolute rounded-full"
          style={{
            left: '50%', top: '50%',
            width: 50, height: 50,
            marginLeft: -25, marginTop: -25,
            border: `${i < 3 ? 1.5 : 1}px solid ${plasmaAccent}`,
          }}
          animate={{
            scale: [1, 5 + i * 0.6],
            opacity: [0.6, 0],
          }}
          transition={{
            duration: 3.5,
            delay: i * 0.5,
            ease: [0.22, 1, 0.36, 1],
            repeat: 1,
            repeatDelay: 0.5,
          }}
        />
      ))}

      {/* 16 частиц — конвергируют к центру, 2 волны */}
      {[...Array(16)].map((_, i) => {
        const angle = (i / 16) * Math.PI * 2;
        const r = 160;
        return (
          <motion.div
            key={`p-${i}`}
            className="absolute rounded-full"
            style={{
              width: i % 3 === 0 ? 5 : 3, height: i % 3 === 0 ? 5 : 3,
              left: '50%', top: '50%',
              background: plasmaAccent,
              boxShadow: `0 0 14px ${plasmaGlow}`,
            }}
            animate={{
              x: [Math.cos(angle) * r, 0, Math.cos(angle + Math.PI) * r * 0.3, 0],
              y: [Math.sin(angle) * r, 0, Math.sin(angle + Math.PI) * r * 0.3, 0],
              opacity: [0, 1, 0.6, 0],
              scale: [0.3, 1.8, 0.8, 0],
            }}
            transition={{
              duration: 5,
              delay: 0.8 + i * 0.12,
              ease: [0.32, 0.72, 0, 1],
            }}
          />
        );
      })}

      {/* Вращающееся кольцо энергии */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 140, height: 140,
          left: '50%', top: '50%',
          marginLeft: -70, marginTop: -70,
          border: `2px solid transparent`,
          borderTop: `2px solid ${plasmaAccent}`,
          borderRight: `2px solid color-mix(in oklab, ${plasmaAccent} 50%, transparent)`,
        }}
        animate={{ rotate: [0, 720], opacity: [0, 0.8, 0.8, 0] }}
        transition={{ duration: 6, delay: 1, ease: 'linear' }}
      />

      {/* Второе кольцо — противоположное вращение */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: 110, height: 110,
          left: '50%', top: '50%',
          marginLeft: -55, marginTop: -55,
          border: `1.5px solid transparent`,
          borderBottom: `1.5px solid ${plasmaAccent}`,
          borderLeft: `1.5px solid color-mix(in oklab, ${plasmaAccent} 40%, transparent)`,
        }}
        animate={{ rotate: [0, -540], opacity: [0, 0.6, 0.6, 0] }}
        transition={{ duration: 7, delay: 1.5, ease: 'linear' }}
      />

      {/* Core icon — появляется, пульсирует, финальная вспышка */}
      <motion.div
        className="relative z-10 flex flex-col items-center mt-6"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.5, delay: 0.3, ease: [0.32, 1.4, 0.32, 1] }}
      >
        <motion.img
          src={icon}
          alt="Plasma Core"
          className="h-28 w-28"
          style={{ filter: `drop-shadow(0 0 30px ${plasmaGlow})` }}
          animate={{
            scale: [1, 1.08, 1, 1.12, 1.05, 1.25, 1.1, 1],
            filter: [
              `drop-shadow(0 0 30px ${plasmaGlow})`,
              `drop-shadow(0 0 50px ${plasmaGlow})`,
              `drop-shadow(0 0 35px ${plasmaGlow})`,
              `drop-shadow(0 0 60px ${plasmaGlow})`,
              `drop-shadow(0 0 40px ${plasmaGlow})`,
              `drop-shadow(0 0 80px ${plasmaGlow})`,
              `drop-shadow(0 0 50px ${plasmaGlow})`,
              `drop-shadow(0 0 35px ${plasmaGlow})`,
            ],
          }}
          transition={{ duration: 7, delay: 0.5, ease: 'easeInOut' }}
        />

        {/* Статус-текст — меняется по фазам */}
        <div className="mt-4 h-5 relative">
          {STATUS_PHASES.map((phase, i) => (
            <motion.p
              key={i}
              className="absolute inset-x-0 text-center text-[9px] uppercase tracking-[0.2em] font-mono font-bold"
              style={{ color: i === STATUS_PHASES.length - 1 ? '#fff' : plasmaAccent }}
              initial={{ opacity: 0, y: 8 }}
              animate={{
                opacity: [0, 1, 1, 0],
                y: [8, 0, 0, -8],
              }}
              transition={{
                duration: i === STATUS_PHASES.length - 1 ? 1.5 : 1.2,
                delay: phase.delay,
                times: [0, 0.15, 0.7, 1],
              }}
            >
              {phase.text}
            </motion.p>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

function FeatureRow({ icon, text, accent }: { icon: React.ReactNode; text: string; accent: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl px-3 py-2" style={{
      background: `color-mix(in oklab, ${accent} 6%, transparent)`,
      border: `1px solid color-mix(in oklab, ${accent} 12%, transparent)`,
    }}>
      <span style={{ color: accent }}>{icon}</span>
      <span className="text-[12px] text-[var(--text-secondary)]">{text}</span>
    </div>
  );
}
